import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  ChevronRightIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { completeChecklistItem, fetchOnboardingManifest } from '../../services/onboardingApi';

function progressPercent(completed, required) {
  if (!required) return 0;
  return Math.min(100, Math.round((completed / required) * 100));
}

export default function ProductTourChecklist({ organizationId }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadManifest = useCallback(() => {
    setLoading(true);
    fetchOnboardingManifest(organizationId)
      .then((data) => {
        setManifest(data);
        if (data?.checklist?.length) setOpen(true);
      })
      .catch((err) => setError(err?.message || 'Could not load product tour'))
      .finally(() => setLoading(false));
  }, [organizationId]);

  useEffect(() => {
    loadManifest();
  }, [loadManifest]);

  const checklist = manifest?.checklist || [];
  const completed = manifest?.checklist_completed_count || 0;
  const required = manifest?.checklist_required_count || checklist.filter((i) => i.required).length;
  const pct = progressPercent(completed, required || checklist.length);

  const nextItem = useMemo(
    () => checklist.find((item) => !item.completed && item.required) || checklist.find((item) => !item.completed),
    [checklist]
  );

  const handleContinue = async () => {
    if (!nextItem) return;
    if (nextItem.deep_link) {
      try {
        await completeChecklistItem(nextItem.item_key, organizationId);
      } catch {
        /* non-blocking */
      }
      navigate(nextItem.deep_link);
      loadManifest();
    }
  };

  if (loading && !manifest) return null;
  if (!checklist.length) return null;

  const allDone = checklist.every((item) => item.completed || !item.required);

  return (
    <div className="fixed bottom-4 right-4 z-40 max-w-sm w-full">
      {open ? (
        <div className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-indigo-600 text-white">
            <div className="flex items-center gap-2">
              <SparklesIcon className="w-5 h-5" />
              <span className="font-semibold text-sm">Your Product Tour</span>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="text-indigo-100 hover:text-white">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          <div className="px-4 py-3">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
              <span>{completed}/{required || checklist.length} complete</span>
              <span>{pct}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full mb-3">
              <div className="h-2 bg-indigo-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
            {nextItem && (
              <p className="text-sm text-gray-700 mb-3">
                <span className="font-medium">Today&apos;s focus:</span> {nextItem.label}
              </p>
            )}
            <ul className="space-y-2 max-h-48 overflow-y-auto mb-3">
              {checklist.map((item) => (
                <li key={item.item_key} className="flex items-start gap-2 text-sm">
                  {item.completed ? (
                    <CheckCircleIcon className="w-5 h-5 text-green-500 shrink-0" />
                  ) : (
                    <span className="w-5 h-5 rounded-full border-2 border-gray-300 shrink-0 mt-0.5" />
                  )}
                  <span className={item.completed ? 'text-gray-400 line-through' : 'text-gray-800'}>
                    {item.label}
                  </span>
                </li>
              ))}
            </ul>
            {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
            {!allDone && nextItem?.deep_link && (
              <button
                type="button"
                onClick={handleContinue}
                className="w-full flex items-center justify-center gap-1 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
              >
                Continue tour
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="ml-auto flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-full shadow-lg hover:bg-indigo-700"
        >
          <SparklesIcon className="w-5 h-5" />
          Product Tour ({completed}/{required || checklist.length})
        </button>
      )}
    </div>
  );
}
